import { Injectable } from '@angular/core'
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, ActivatedRoute } from '@angular/router'
import { CoreService } from './core.service'
import { OAuthSession, OAuthToken } from 'depoto-core/src/models'
import { NotificationService } from './notification.service'
import { SchemaUtil } from '../utils'
import { User } from 'depoto-core/src/entities/User'
import { Events } from 'depoto-core/src/utils'
import { TranslateService } from '@ngx-translate/core'
import { MultiSessionService } from './multi-session.service'

@Injectable({
  providedIn: 'root',
})
export class AuthGuardService {
  session: OAuthSession

  constructor(
    private router: Router,
    private core: CoreService,
    public notificationService: NotificationService,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private multiSession: MultiSessionService
  ) {
    this.route.queryParams.subscribe(params => {
      if (!params?.access_token || !params?.refresh_token) {
        return
      }
      // Nothing in this app ever builds a URL with these two query params except the
      // /external-login hand-off link - so this block and canActivate()'s dedicated
      // 'external-login' branch below both fire for that same navigation. That branch already
      // fully owns it (sets isRememberMe, persists every storage key, registers the multi-session
      // entry); this block's OAuthSession never sets isRememberMe (defaults to false), so
      // whichever handler's oauth.session assignment landed last decided the outcome. Losing the
      // race left isRememberMe false, which OAuthService.getRefreshToken() reads as "don't
      // remember this session" and silently logs it out instead of refreshing - the "expired"
      // bug external-login accounts hit. Bail out here so canActivate() is the only one handling it.
      if (this.router.url.split('?')[0] === '/external-login') {
        return
      }

      this.core.services.oauth.session = new OAuthSession({
        clientType: (this.session.clientType = this.core.services.storage.getSync(
          this.core.services.storage.keys.auth.clientType
        )),
        token: new OAuthToken({
          accessToken: params.access_token,
          refreshToken: params.refresh_token,
          tokenType: params.token_type,
          scope: params.scope,
          expiresIn: params.expires_in,
        }),
      })

      this.loadUserProfile().then(async () => {
        // Intentionally disabled: cleanLocalStorage(..., 'login') deletes other users' saved view settings.
        // We can remove this commented code later on if everything will be working fine even without this.
        // try {
        //   const emailFromUser = this.core.services.user.user?.email
        //   const emailFromStorage = this.core.services.storage.getSync(this.core.services.storage.keys.auth.email)
        //   const sessionEmail = this.core.services.oauth.session.email
        //   const userEmail = emailFromUser || emailFromStorage || sessionEmail
        //   if (userEmail) this.core.cleanLocalStorage(userEmail, 'login')
        // } catch (e) {
        //   console.warn('Failed to clean localStorage on login', e)
        // }

        this.core.services.oauth.session.isAuthenticated = true
        this.router.navigate(['/home'])
      })
    })
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    // The tab's very first guarded navigation fires concurrently with MultiSessionService's own
    // constructor-kicked-off session verification (opened via a session-item link - see
    // buildOpenSessionUrl()), which is already doing everything below on its own: restoring the
    // session, fetching the profile, saving it. Piling this guard's own copy of that on top would
    // at best duplicate the getMyProfile() request and at worst abort the verification's request
    // mid-flight (this.core.services.http.abortAllRequests() below) or stomp its result with
    // whatever's in the shared auth.* storage slot, turning a perfectly valid session into a
    // false "switch failed". Let the verification own the outcome; isAuthenticated() reacting to
    // it is enough to reveal the already-resolved route once it settles (app.component.html gates
    // the router-outlet content on isAuthenticated() separately from route resolution).
    if (this.multiSession.isPendingOpenSessionInFlight) {
      return true
    }
    this.core.services.http.abortAllRequests()
    this.multiSession.onAppStartup()
    if (route.routeConfig.path === 'external-login') {
      try {
        this.session = new OAuthSession()
        this.session.token.accessToken = route.queryParams.access_token
        // Store the hand-off refresh_token exactly as received. The Depoto OAuth server issues
        // (and expects back) refresh tokens in an opaque base64 wire form - the same form
        // grant_type=password responses return and the modal-login refresh path resends verbatim
        // to succeed. The hand-off link already carries that form; decoding/unwrapping it here
        // only mangles it into something the server rejects as "invalid_grant". The permanent
        // invalid_grant this branch used to hit came from the clientType bug below (a prod token
        // refreshed against the stage endpoint), not from the token's encoding.
        this.session.token.refreshToken = route.queryParams.refresh_token
        this.session.token.tokenType = route.queryParams.token_type
        this.session.token.scope = route.queryParams.scope
        this.session.token.expiresIn = Number(route.queryParams.expires_in)
        this.session.token.timestamp = +new Date()
        // Strip a trailing slash before comparing - every other reference to this URL in the app
        // (SERVER_URLS in multi-session.service.ts, ApiUtils.ts, graphql-client.ts) uses the
        // trailing-slash form. A caller sending serverUrl in that same, more common form used to
        // silently fall through to 'stage' here, storing the wrong clientType for a prod account.
        // MultiSessionService.refreshEntryToken() then refreshes it against the stage token
        // endpoint with stage client credentials - the server correctly, and permanently, rejects
        // that as invalid, so every later switch back to the account failed as "expired" even
        // though the token itself was fine.
        const normalizedServerUrl = (route.queryParams.serverUrl ?? '').replace(/\/+$/, '')
        this.session.clientType = normalizedServerUrl === 'https://server1.depoto.cz' ? 'prod' : 'stage'
        this.session.isAuthenticated = true
        this.session.isRememberMe = true
        this.core.services.oauth.session = this.session
        // Flat, matching OAuthService.saveData()'s own shape for this key everywhere else in the
        // app - it was previously wrapped in an extra { token: {...}, clientType } layer here only,
        // so anything reading this key back via oauth.loadData()/loadDataSync() (e.g. a reload, or
        // MultiSessionService.restoreSessionForTab()) would find session.token nested one level too
        // deep and see accessToken/refreshToken as undefined.
        this.core.services.storage.set(this.core.services.storage.keys.auth.data, this.session.token)
        this.core.services.storage.set(
          this.core.services.storage.keys.auth.isAuthenticated,
          this.session.isAuthenticated
        )
        this.core.services.storage.set(this.core.services.storage.keys.auth.clientType, this.session.clientType)
        this.core.services.storage.set(this.core.services.storage.keys.auth.rememberMe, this.session.isRememberMe)
      } catch (err) {
        console.error(err)
      } finally {
        const userProfile = await this.core.services.user.getMyProfile(SchemaUtil.userProfile)
        const user = new User(userProfile)
        this.core.services.storage.set(this.core.services.storage.keys.auth.company, user.company)
        this.core.services.storage.set(this.core.services.storage.keys.auth.email, user.email)
        // saveCurrentSession() below requires the email on the in-memory oauth session —
        // it's never set earlier in this branch, so without this the external-login user
        // silently never gets added to the multi-session list.
        this.core.services.oauth.session.email = user.email
        await this.core.services.user.setCurrentUser(user.email)
        // Intentionally disabled: cleanLocalStorage(..., 'login') deletes other users' saved view settings.
        // We can remove this commented code later on if everything will be working fine even without this.
        // try {
        //   this.core.cleanLocalStorage(user.email, 'login')
        // } catch (e) {
        //   console.warn('Failed to clean localStorage on login', e)
        // }
        this.multiSession.saveCurrentSession()
        // Move off the hand-off URL now that everything from it is persisted. Leaving the
        // access/refresh tokens sitting in the address bar/history is a credential-exposure risk
        // on its own, but it also breaks later account switches: box-user's switchTo() re-enters
        // router.url to force the current route to remount on the new account, and if that URL is
        // still this one, it re-runs this whole branch with the *old* account's now-stale token -
        // silently switching the active user back. replaceUrl keeps the token link out of history.
        this.router.navigate(['/home'], { replaceUrl: true })
      }
      return true
    }

    type SessionData = {
      clientType: string
      token: any
    }
    const sessionDataInLocalStorage: SessionData = await this.core.services.storage.get({
      key: 'auth_data',
      type: 'Object',
    })
    if (sessionDataInLocalStorage?.token) {
      this.core.services.oauth.session.clientType = sessionDataInLocalStorage.clientType
      this.core.services.oauth.session.token = sessionDataInLocalStorage.token
    }

    this.core.services.oauth.session.clientType = this.core.services.storage.getSync(
      this.core.services.storage.keys.auth.clientType
    )

    const oauth = this.core.services.oauth
    const hasSupportedClientType = Object.prototype.hasOwnProperty.call(
      oauth.defaultClientKeys,
      oauth.session.clientType
    )
    if (!hasSupportedClientType || !oauth.isAuthenticated() || !oauth.getAccessToken()) {
      // Login is embedded in AppComponent, so allow the route to activate without
      // issuing authenticated requests while there is no usable session.
      oauth.session.isAuthenticated = false
      return true
    }
    if (!this.core.services.user.user?.id) {
      await this.loadUserProfile()
      // } else if (!this.core.lists.allDepots?.length) { todo
      //   await this.core.loadLists() todo
    }
    // this.multiSession.saveCurrentSession()
    // await this.checkRoles(route, state)
    // return true // embedded login
    if (oauth.isAuthenticated()) {
      await this.checkRoles(route, state)
    }
    return true // embedded login
  }

  private async checkRoles(route, state) {
    if (
      !this.core.services.user.hasRole('ROLE_SUPER_ADMIN') &&
      !this.core.services.user.hasRole('ROLE_ADMIN') &&
      ((state.url === '/products' && !this.core.services.user.hasRole('ROLE_PRODUCT_LIST')) ||
        (state.url === '/product' && !this.core.services.user.hasRole('ROLE_PRODUCT_CREATE')) ||
        (route.routeConfig.path === 'product/:id' && !this.core.services.user.hasRole('ROLE_PRODUCT_SHOW')) ||
        (state.url === '/stock-batch-out' && !this.core.services.user.hasRole('CLIENT_MENU_OUT')) ||
        (state.url === '/stock-batch-in' && !this.core.services.user.hasRole('CLIENT_MENU_IN')) ||
        (state.url === '/stock-batch-transfer' && !this.core.services.user.hasRole('CLIENT_MENU_TRANSFER')) ||
        ((state.url === '/product-move-packs' || state.url === '/product-move-pack') &&
          !this.core.services.user.hasRole('ROLE_PRODUCT_MOVE_PACK_LIST')) ||
        (route.routeConfig.path === 'product-move-pack/:id' &&
          !this.core.services.user.hasRole('ROLE_PRODUCT_MOVE_PACK_SHOW')) ||
        ((state.url === '/inventory-exports' || route.routeConfig.path === 'inventory-detail/:id') &&
          !this.core.services.user.hasRole('CLIENT_MENU_INVENTORY')) ||
        ((state.url === '/users' || route.routeConfig.path === '/user') &&
          !this.core.services.user.hasRole('ROLE_USER_LIST')) ||
        (route.routeConfig.path === 'user/:id' && !this.core.services.user.hasRole('ROLE_USER_SHOW')) ||
        ((state.url === '/categories' || state.url === '/category') &&
          !this.core.services.user.hasRole('ROLE_CATEGORY_LIST')) ||
        (route.routeConfig.path === 'category/:id' && !this.core.services.user.hasRole('ROLE_CATEGORY_SHOW')) ||
        ((state.url === '/depots' || state.url === '/depot') && !this.core.services.user.hasRole('ROLE_DEPOT_LIST')) ||
        (route.routeConfig.path === 'depot/:id' && !this.core.services.user.hasRole('ROLE_DEPOT_SHOW')) ||
        ((state.url === '/institutions' || state.url === '/institution') &&
          !this.core.services.user.hasRole('ROLE_INSTITUTION_SHOW')) ||
        (route.routeConfig.path === 'institution/:id' && !this.core.services.user.hasRole('ROLE_INSTITUTION_SHOW')) ||
        ((state.url === '/surgeries' || state.url === '/surgery') &&
          !this.core.services.user.hasRole('ROLE_CHECKOUT_LIST')) ||
        (route.routeConfig.path === 'surgery/:id' && !this.core.services.user.hasRole('ROLE_CHECKOUT_SHOW')) ||
        ((state.url === '/suppliers' || state.url === '/supplier') &&
          !this.core.services.user.hasRole('ROLE_SUPPLIER_LIST')) ||
        (route.routeConfig.path === 'supplier/:id' && !this.core.services.user.hasRole('ROLE_SUPPLIER_SHOW')))
    ) {
      this.notificationService.error(this.translateService.instant('access-denied'))
      return false
    } else {
      return true
    }
  }

  async loadUserProfile() {
    this.core.services.user.currentUserEmitter.subscribe().then(user => {
      if (user?.id > 0) {
        // todo co ten event?
        Events.dispatch('app:user:loaded')
        // todo nacte se spravne, jsou data k dispozici?
        // this.core.loadLists()
      }
    })

    let user: User | undefined
    try {
      user = await this.core.services.user.getMyProfile(SchemaUtil.userProfile)
    } catch (e) {
      console.warn(e)
      if (e && e.status === 401) {
        // Sentry.captureException(e)
      }
    }
    if (user?.email) {
      this.core.services.storage.set(
        this.core.services.storage.keys.auth.companyCarriers,
        user.company.carrierRelations
      )
      this.core.services.user.afterSetCurrentUser(user)
    } else {
      this.core.services.oauth.logout()
    }
  }
}
